/**
 * RoleEditor — Composant partagé (V10 Hotfix Pilote).
 *
 * Édite les rôles fonctionnels (`athlete`, `coach`, `official`, `volunteer`) d'un
 * User. Utilisé à la fois par la page `/admin/personnes/:userId/roles` et par
 * le modal "Modifier les rôles" de la Console de modération.
 *
 * Comportement :
 *  - GET  /api/admin/users/:userId/roles  → snapshot + impact preview
 *  - PATCH /api/admin/users/:userId/roles → applique (avec confirm_revoke_links si nécessaire)
 *
 * Multi-rôle strict : cases à cocher indépendantes, aucun switch "transformer en".
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Loader2, Save, ShieldCheck, AlertTriangle, Users, Trophy } from "lucide-react";
const EDITABLE_ROLES = [
  {
    key: "athlete",
    label: "Athlète",
    desc: "Fiche athlète publique (palmarès, résultats). L'ajout republie une fiche existante ; le retrait dépublie sans supprimer l'historique.",
    icon: Trophy,
  },
  {
    key: "coach",
    label: "Coach / Encadrant",
    desc: "Fiche Coach auto-liée (jamais de doublon). L'ajout la rend visible dans l'annuaire Coach et les sélecteurs. Le retrait la masque sans effacer l'historique.",
    icon: Users,
  },
  {
    key: "official",
    label: "Officiel",
    desc: "Arbitre, juge, dirigeant.",
    icon: ShieldCheck,
  },
  {
    key: "volunteer",
    label: "Bénévole",
    desc: "Communauté AITO.",
    icon: ShieldCheck,
  },
];

export function RoleEditor({ userId, onSaved, autoFocus = false, hideHeader = false }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null); // { coach_active_affiliations, athlete_active_affiliations }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/roles`);
      setSnapshot(data);
      setSelected(new Set((data.roles || []).filter((r) => EDITABLE_ROLES.some((e) => e.key === r))));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les rôles.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const previousSet = useMemo(() => new Set(snapshot?.roles || []), [snapshot]);
  const added = useMemo(
    () => Array.from(selected).filter((r) => !previousSet.has(r)),
    [selected, previousSet]
  );
  const removed = useMemo(
    () => Array.from(previousSet).filter((r) => EDITABLE_ROLES.some((e) => e.key === r) && !selected.has(r)),
    [selected, previousSet]
  );

  const willRevokeCoachLinks = removed.includes("coach") && (snapshot?.coach?.active_affiliations_count || 0) > 0;
  const willRevokeAthleteLinks = removed.includes("athlete") && (snapshot?.athlete_active_affiliations_count || 0) > 0;
  const needsConfirm = willRevokeCoachLinks || willRevokeAthleteLinks;

  const save = async ({ withConfirm = false } = {}) => {
    if (!snapshot) return;
    setBusy(true);
    try {
      const preservedStaff = (snapshot.roles || []).filter((r) => !EDITABLE_ROLES.some((e) => e.key === r));
      const finalRoles = [...preservedStaff, ...Array.from(selected)];
      const { data } = await api.patch(`/admin/users/${userId}/roles`, {
        roles: finalRoles,
        reason: reason.trim() || null,
        confirm_revoke_links: withConfirm,
      });
      if (data.requires_confirmation) {
        setConfirmModal({
          coach: data.coach_active_affiliations || 0,
          athlete: data.athlete_active_affiliations || 0,
        });
        setBusy(false);
        return;
      }
      const parts = [];
      if (data.added?.length) parts.push(`+ ${data.added.join(", ")}`);
      if (data.removed?.length) parts.push(`- ${data.removed.join(", ")}`);
      const rev = (data.revoked_links || []).reduce((s, r) => s + (r.count || 0), 0);
      if (rev > 0) parts.push(`${rev} affiliation(s) révoquée(s)`);
      toast.success(`Rôles mis à jour · ${parts.join(" · ") || "aucun changement"}`);
      setConfirmModal(null);
      await load();
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3" data-testid="role-editor-loading">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }
  if (!snapshot) return null;

  const user = snapshot.user || {};

  return (
    <div className="space-y-4" data-testid="role-editor">
      {!hideHeader && (
        <div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            <span className="font-semibold">Rôles du profil</span>
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {user.name || user.email} — rôles actuels :{" "}
            {(snapshot.roles || []).length === 0
              ? <span className="italic">aucun</span>
              : (snapshot.roles || []).map((r) => (
                  <Badge key={r} variant="secondary" className="mr-1 text-[10px]">{r}</Badge>
                ))
            }
          </div>
        </div>
      )}

      {/* Cases à cocher */}
      <div className="space-y-2">
        {EDITABLE_ROLES.map((r, idx) => {
          const active = selected.has(r.key);
          const Icon = r.icon;
          const isCoachRow = r.key === "coach";
          const isAthleteRow = r.key === "athlete";
          return (
            <label
              key={r.key}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                active
                  ? "border-[hsl(var(--aito-lagoon))] bg-[hsl(var(--aito-lagoon))]/10"
                  : "hover:bg-muted"
              }`}
              data-testid={`role-editor-row-${r.key}`}
            >
              <Checkbox
                checked={active}
                onCheckedChange={() => toggle(r.key)}
                autoFocus={autoFocus && idx === 0}
                data-testid={`role-editor-checkbox-${r.key}`}
              />
              <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-sm flex items-center gap-2">
                  {r.label}
                  {isCoachRow && snapshot.coach?.exists && (
                    <Badge variant="outline" className="text-[9px]">
                      Fiche liée · {snapshot.coach.published ? "publiée" : "dépubliée"}
                    </Badge>
                  )}
                  {isAthleteRow && (snapshot.athletes || []).length > 0 && (
                    <Badge variant="outline" className="text-[9px]">
                      {snapshot.athletes.length} fiche(s) liée(s)
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{r.desc}</div>
              </div>
            </label>
          );
        })}
      </div>

      {/* Warning affiliations actives */}
      {needsConfirm && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs space-y-1"
          data-testid="role-editor-warning-affiliations"
        >
          <div className="flex items-center gap-1.5 font-semibold text-amber-900 dark:text-amber-200">
            <AlertTriangle className="h-3.5 w-3.5" />
            Affiliations actives à révoquer
          </div>
          {willRevokeCoachLinks && (
            <p className="text-amber-800 dark:text-amber-300" data-testid="role-editor-warning-coach">
              Ce coach est actuellement lié à <strong>{snapshot.coach.active_affiliations_count}</strong> athlète(s).
              Le retrait du rôle Coach <strong>révoquera ces affiliations</strong> (soft — historique conservé).
            </p>
          )}
          {willRevokeAthleteLinks && (
            <p className="text-amber-800 dark:text-amber-300" data-testid="role-editor-warning-athlete">
              Cet athlète a <strong>{snapshot.athlete_active_affiliations_count}</strong> affiliation(s) active(s)
              (club/fédération/coach). Le retrait du rôle Athlete <strong>révoquera ces affiliations</strong>.
            </p>
          )}
        </div>
      )}

      {/* Motif optionnel */}
      <div className="space-y-1">
        <Label className="text-xs">Motif (optionnel · consigné dans audit_logs)</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex : demande RGPD, promotion Athlète→Coach, correction erreur d'import…"
          rows={2}
          data-testid="role-editor-reason"
        />
      </div>

      <div className="flex justify-end pt-2 border-t">
        <Button
          onClick={() => save({ withConfirm: false })}
          disabled={busy || (added.length === 0 && removed.length === 0)}
          data-testid="role-editor-save"
        >
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Enregistrer les rôles
        </Button>
      </div>

      {/* V11 Hotfix — Panneau sélection JP 2027 (réutilise endpoints games-selections) */}
      {snapshot.athletes && snapshot.athletes.length > 0 && (
        <div className="border-t pt-3">
          <JpSelectionMini athletes={snapshot.athletes} onChanged={load} />
        </div>
      )}

      {/* Modal de confirmation soft-révocation */}
      {confirmModal && (
        <ConfirmRevokeModal
          coach={confirmModal.coach}
          athlete={confirmModal.athlete}
          busy={busy}
          onCancel={() => setConfirmModal(null)}
          onConfirm={() => save({ withConfirm: true })}
        />
      )}
    </div>
  );
}

function ConfirmRevokeModal({ coach, athlete, busy, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="role-editor-confirm-modal"
    >
      <div className="max-w-md w-full bg-background rounded-xl border shadow-xl p-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h3 className="font-display text-lg font-bold">Confirmer la révocation</h3>
        </div>
        <div className="text-sm space-y-1 text-muted-foreground">
          {coach > 0 && (
            <p data-testid="role-editor-confirm-coach">
              Le rôle <strong>Coach</strong> sera retiré. <strong>{coach} affiliation(s)</strong> Athlète→Coach active(s)
              seront révoquées (soft — historique conservé, non affichées publiquement).
            </p>
          )}
          {athlete > 0 && (
            <p data-testid="role-editor-confirm-athlete">
              Le rôle <strong>Athlete</strong> sera retiré. <strong>{athlete} affiliation(s)</strong> active(s)
              seront révoquées. L'historique reste consultable en admin.
            </p>
          )}
          <p className="text-xs text-muted-foreground pt-1">
            Cette action est journalisée dans <code>audit_logs</code>. Un ré-ajout ultérieur du rôle republiera la fiche mais ne restaurera pas automatiquement les affiliations.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="role-editor-confirm-cancel">
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={busy}
            data-testid="role-editor-confirm-apply"
          >
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirmer et révoquer
          </Button>
        </div>
      </div>
    </div>
  );
}

export default RoleEditor;

// ---------------------------------------------------------------------------
// V11 Hotfix — JpSelectionMini
// Réutilise les endpoints `/api/games-selections/*` existants (aucun nouveau mécanisme).
// ---------------------------------------------------------------------------
function JpSelectionMini({ athletes, onChanged }) {
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const athlete = athletes[0]; // premier athlete (cas standard : 1 par user)

  const refresh = useCallback(async () => {
    if (!athlete?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/games-selections/athlete/${athlete.id}`);
      setSel(data.selection);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [athlete?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const doAction = async (kind, { discipline, reason } = {}) => {
    setBusy(true);
    try {
      if (kind === "declare") {
        await api.post(`/games-selections/athlete/${athlete.id}/declare`, { discipline: discipline || null });
      } else if (kind === "admin_select") {
        await api.post(`/games-selections/admin/create`, { athlete_id: athlete.id });
      } else if (sel?.id) {
        await api.patch(`/games-selections/${sel.id}/${kind}`, { reason: reason || null });
      }
      toast.success("Sélection JP 2027 mise à jour.");
      await refresh();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible.");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-xs text-muted-foreground">Chargement JP 2027…</div>;

  const status = sel?.status;
  return (
    <div data-testid="role-editor-jp-mini">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Trophy className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
        Sélection Jeux du Pacifique 2027
        {status && (
          <Badge variant="outline" className="text-[9px] uppercase" data-testid="jp-mini-status">{status}</Badge>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground mt-0.5">
        {athlete.full_name}{sel?.discipline ? ` · ${sel.discipline}` : ""}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {!sel && (
          <Button size="sm" onClick={() => doAction("admin_select")} disabled={busy} data-testid="jp-mini-admin-select">
            <Trophy className="h-3 w-3 mr-1" />Sélectionner (SELECTED)
          </Button>
        )}
        {!sel && (
          <Button size="sm" variant="outline" onClick={() => doAction("declare")} disabled={busy} data-testid="jp-mini-declare">
            Créer PENDING
          </Button>
        )}
        {status === "pending" && (
          <>
            <Button size="sm" onClick={() => doAction("validate")} disabled={busy} data-testid="jp-mini-validate">Valider</Button>
            <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => doAction("reject", { reason: "Admin (RoleEditor)" })} disabled={busy} data-testid="jp-mini-reject">Refuser</Button>
          </>
        )}
        {status === "selected" && (
          <Button size="sm" variant="outline" className="text-amber-700 hover:text-amber-700" onClick={() => doAction("revoke", { reason: "Admin (RoleEditor)" })} disabled={busy} data-testid="jp-mini-revoke">Révoquer</Button>
        )}
        {(status === "rejected" || status === "revoked") && (
          <Button size="sm" onClick={() => doAction("admin_select")} disabled={busy} data-testid="jp-mini-readmit">
            Ré-inscrire (SELECTED)
          </Button>
        )}
      </div>
    </div>
  );
}
